import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiUser, FiLock, FiUsers, FiBriefcase } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'team' | 'hr'>('team');
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Demo credentials
  const TEAM_CREDENTIALS = { email: 'team@miranimarketing.com', password: 'team123' };
  const HR_CREDENTIALS = { email: 'hr@miranimarketing.com', password: 'hr123' };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      const validCredentials = activeTab === 'team' ? TEAM_CREDENTIALS : HR_CREDENTIALS;
      
      if (credentials.email === validCredentials.email && credentials.password === validCredentials.password) {
        // Store auth token and user type in localStorage
        localStorage.setItem('authToken', 'demo-token-' + activeTab);
        localStorage.setItem('userType', activeTab);
        
        // Navigate to respective dashboard
        if (activeTab === 'team') {
          navigate('/admin/blog');
        } else {
          navigate('/admin/careers');
        }
      } else {
        setError('Invalid email or password');
      }
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Mirani Marketing
          </h1>
          <p className="text-gray-600">Admin Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Tab Selector */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('team')}
              className={`flex-1 py-4 px-6 font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                activeTab === 'team'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FiUsers className="w-5 h-5" />
              Team Login
            </button>
            <button
              onClick={() => setActiveTab('hr')}
              className={`flex-1 py-4 px-6 font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                activeTab === 'hr'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FiBriefcase className="w-5 h-5" />
              HR Login
            </button>
          </div>

          {/* Login Form */}
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {activeTab === 'team' ? 'Blog Management' : 'Career Management'}
              </h2>
              <p className="text-gray-600 text-sm">
                {activeTab === 'team'
                  ? 'Access the blog dashboard to create, edit, and manage blog posts'
                  : 'Access the careers dashboard to post and manage job openings'}
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={credentials.email}
                    onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter your password"
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </motion.button>
            </form>

            {/* Demo Credentials Info */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-semibold text-gray-700 mb-2">Demo Credentials:</p>
              <div className="text-xs text-gray-600 space-y-1">
                {activeTab === 'team' ? (
                  <>
                    <p><strong>Email:</strong> team@miranimarketing.com</p>
                    <p><strong>Password:</strong> team123</p>
                  </>
                ) : (
                  <>
                    <p><strong>Email:</strong> hr@miranimarketing.com</p>
                    <p><strong>Password:</strong> hr123</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-sm mt-6">
          © 2025 Mirani Marketing. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
};

export default LoginPage;
